import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
// @mui
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
//
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { BackendValidationError } from 'src/utils/backend-validation-error';
import { IftaFiling, UploadResponse } from 'src/models';
//
import FileDrop from './file-drop';
import ColumnMappingCard from './column-mapping-card';
import { FuelPreviewTable } from './parsed-preview-table';
import SkipCard from './skip-card';
import UploadedFilesList from './uploaded-files-list';

import StepTitle from './step-title';

// ----------------------------------------------------------------------

type Props = {
  onBack?: () => void;
  onSkip: () => void;
  filing: IftaFiling;
  onUploaded: (filing: IftaFiling) => void;
  onContinue: () => void;
};

export default function StepFuelUpload({ filing, onUploaded, onContinue, onBack, onSkip }: Props) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResponse | null>(null);

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post<UploadResponse>(
        formatEndpoint(endpoints.filings.uploadFuel, { id: filing.id }),
        formData
      );
      setResult(response.data);
      onUploaded(response.data.filing);
    } catch (error) {
      const messages: BackendValidationError[] = error?.response?.data;
      if (Array.isArray(messages)) {
        messages.forEach((message) => enqueueSnackbar(message.message, { variant: 'error' }));
      } else {
        enqueueSnackbar('Something went wrong uploading your file', { variant: 'error' });
      }
    } finally {
      setUploading(false);
    }
  };

  const previewLines = filing.fuelLines;
  const hasData = previewLines.length > 0;

  return (
    <Stack spacing={3}>
      <StepTitle title="Your fuel card report" onBack={onBack} />
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Upload the fuel purchase report from your fuel card provider (EFS, TCH, Comdata…). We use it
        for the tax-paid gallons per state.
      </Typography>

      <UploadedFilesList
        filingId={filing.id}
        files={filing.fuelFiles}
        lineCountFor={(fileName) => filing.fuelLines.filter((line) => line.importedFromFile === fileName).length}
        removeEndpoint={endpoints.filings.removeFuelFile}
        onRemoved={onUploaded}
      />

      <FileDrop
        uploading={uploading}
        onFile={uploadFile}
        hint={
          filing.fuelFiles.length > 0
            ? 'Add another fuel card report (CSV or XLSX)'
            : 'Fuel card transactions (CSV or XLSX)'
        }
      />

      {result?.mappingSource === 'ai' && (
        <ColumnMappingCard mapping={result.mapping} headers={result.headers} kind="fuel" />
      )}

      {result && result.unknownJurisdictions.length > 0 && (
        <Typography variant="body2" sx={{ color: 'warning.dark' }}>
          Skipped non-IFTA jurisdictions: {result.unknownJurisdictions.join(', ')}
        </Typography>
      )}

      {hasData && (
        <Card variant="outlined">
          <FuelPreviewTable lines={previewLines} />
        </Card>
      )}

      {hasData && (
        <Button
          variant="contained"
          color="secondary"
          size="large"
          onClick={onContinue}
          sx={{ alignSelf: 'flex-start' }}
          data-testid="fuel-continue"
        >
          Looks good, continue
        </Button>
      )}

      {!hasData && (
        <SkipCard
          title="Waiting on your fuel card statement?"
          body="If you're still waiting on the transaction report from your fuel card provider, skip ahead and add it later. We can't calculate your filing until it's uploaded."
          onSkip={onSkip}
        />
      )}
    </Stack>
  );
}
